import {
  CreateInvestmentPayload,
  InvestmentsDetails,
  InvestmentsTypes,
  UpdateInvestmentPayload,
} from "../../pages/Investments";
import { MarketUpdate } from "../../pages/Investments/panels";
import { WindowType } from "../../utils/types";
import { baseApi } from "../api";
import {
  contextParams,
  SectionEnvelopeWire,
  SectionRequestArgs,
  SectionResponse,
  toSectionResponse,
  withRejectionStatus,
} from "../consistency";
import {
  InvestmentHistoryResponseWire,
  InvestmentsDetailsWire,
  InvestmentsHistoryResponseWire,
  InvestmentsResponseWire,
  toInvestmentHistoryResponse,
  toInvestmentsDetails,
  toInvestmentsHistoryResponse,
  toInvestmentsResponse,
} from "./canonical";
import {
  InvestmentHistoryResponse,
  InvestmentMarketUpdateItem,
  InvestmentTransfersResponse,
  InvestmentsHistoryResponse,
  InvestmentsResponse,
  PaginatedResponse,
} from "./types";

interface GetInvestmentsArgs {
  activeOnly?: boolean;
  year?: number;
}

interface GetInvestmentsHistoryArgs {
  activeOnly?: boolean;
  window?: WindowType;
  year?: number;
}

interface UpdateInvestmentArgs {
  id: number;
  payload: UpdateInvestmentPayload;
}

interface UpdateInvestmentAccountStatusArgs {
  id: number;
  isActive: boolean;
}

interface GetInvestmentHistoryArgs extends SectionRequestArgs {
  id: number;
  window: string;
}

interface GetPaginatedArgs {
  id: number;
  page: number;
  limit: number;
}

interface CreateMarketUpdateArgs {
  id: number;
  payload: MarketUpdate;
}

interface UpdateMarketUpdateArgs {
  id: number;
  marketUpdateId: number;
  payload: MarketUpdate;
}

interface DeleteMarketUpdateArgs {
  id: number;
  marketUpdateId: number;
}

const investmentsListTags = (result?: InvestmentsResponse) => [
  { type: "Investment" as const, id: "LIST" },
  ...(result?.investments.map((i) => ({
    type: "Investment" as const,
    id: i.id,
  })) ?? []),
];

/**
 * Full invalidation for an investment write (create/update/status): the
 * row and list, its history, the years list, and net-worth (`Finances`).
 */
const invalidateInvestment = (id: number) => [
  { type: "Investment" as const, id },
  { type: "Investment" as const, id: "LIST" },
  { type: "InvestmentHistory" as const, id },
  { type: "InvestmentHistory" as const, id: "LIST" },
  "InvestmentYears" as const,
  "Finances" as const,
];

/** Delete skips the per-id tags: the detail page is still mounted and would refetch the deleted row. */
const invalidateDeletedInvestment = () => [
  { type: "Investment" as const, id: "LIST" },
  { type: "InvestmentHistory" as const, id: "LIST" },
  "InvestmentYears" as const,
  "Finances" as const,
];

/**
 * Sub-resource write (market update): refresh the parent detail, its list,
 * history, the market-update list itself, and net-worth. No `InvestmentYears`
 * since a market update never changes which years have investments.
 */
const invalidateInvestmentSubResource = (id: number) => [
  { type: "Investment" as const, id },
  { type: "Investment" as const, id: "LIST" },
  { type: "InvestmentHistory" as const, id },
  { type: "InvestmentHistory" as const, id: "LIST" },
  { type: "InvestmentMarketUpdate" as const, id },
  "Finances" as const,
];

/**
 * Investments RTK Query endpoints. Follows the reference `liabilitiesApi`
 * shape: queries tag with `providesTags`, mutations invalidate those tags so
 * the cache refetches automatically (no hand-rolled `refetch`). Market-update
 * sub-resource writes invalidate the parent detail + list + history +
 * market-update list + `Finances`.
 */
export const investmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvestments: builder.query<
      InvestmentsResponse,
      GetInvestmentsArgs | void
    >({
      query: (args) => {
        const { activeOnly = true, year } = args ?? {};
        return {
          url: "/investments",
          params: year === undefined ? { activeOnly } : { activeOnly, year },
        };
      },
      transformResponse: (response: InvestmentsResponseWire) =>
        toInvestmentsResponse(response),
      providesTags: investmentsListTags,
    }),

    getInvestmentsHistory: builder.query<
      InvestmentsHistoryResponse,
      GetInvestmentsHistoryArgs | void
    >({
      query: (args) => {
        const { activeOnly = true, window = WindowType.ALL, year } = args ?? {};
        return {
          url: "/investments/history",
          // The API rejects a year combined with a window.
          params:
            year === undefined ? { activeOnly, window } : { activeOnly, year },
        };
      },
      transformResponse: (response: InvestmentsHistoryResponseWire) =>
        toInvestmentsHistoryResponse(response),
      providesTags: [{ type: "InvestmentHistory", id: "LIST" }],
    }),

    getInvestmentsYears: builder.query<number[], void>({
      query: () => ({ url: "/investments/years" }),
      providesTags: ["InvestmentYears"],
      keepUnusedDataFor: 300,
    }),

    getInvestmentTypes: builder.query<InvestmentsTypes[], void>({
      query: () => ({ url: "/investments/types" }),
      providesTags: ["InvestmentTypes"],
      keepUnusedDataFor: 300,
    }),

    getInvestment: builder.query<InvestmentsDetails, number>({
      query: (id) => ({ url: "/investments/" + id }),
      transformResponse: (response: InvestmentsDetailsWire) =>
        toInvestmentsDetails(response),
      providesTags: (_result, _error, id) => [{ type: "Investment", id }],
    }),

    // Versioned like `getHoldings`: with a context it provides no tags, and the summary's next pair re-keys it.
    getInvestmentHistory: builder.query<
      SectionResponse<InvestmentHistoryResponse>,
      GetInvestmentHistoryArgs
    >({
      query: ({ id, window, context }) => ({
        url: "/investments/" + id + "/history",
        params: { window, ...contextParams(context) },
        unwrapResult: false,
      }),
      transformResponse: (
        response: SectionEnvelopeWire<InvestmentHistoryResponseWire>,
      ) => toSectionResponse(response, toInvestmentHistoryResponse),
      transformErrorResponse: withRejectionStatus,
      providesTags: (_result, _error, { id, context }) =>
        context ? [] : [{ type: "InvestmentHistory", id }],
    }),

    getInvestmentTransfers: builder.query<
      InvestmentTransfersResponse,
      GetPaginatedArgs
    >({
      query: ({ id, page, limit }) => ({
        url: "/investments/" + id + "/transfers",
        params: { page, limit },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "InvestmentTransfer", id },
      ],
    }),

    getInvestmentMarketUpdates: builder.query<
      PaginatedResponse<InvestmentMarketUpdateItem>,
      GetPaginatedArgs
    >({
      query: ({ id, page, limit }) => ({
        url: "/investments/" + id + "/market_updates",
        params: { page, limit },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "InvestmentMarketUpdate", id },
      ],
    }),

    createInvestment: builder.mutation<unknown, CreateInvestmentPayload>({
      query: (payload) => ({
        url: "/investments",
        method: "post",
        data: payload,
      }),
      invalidatesTags: [
        { type: "Investment", id: "LIST" },
        { type: "InvestmentHistory", id: "LIST" },
        "InvestmentYears",
        "Finances",
      ],
    }),

    updateInvestment: builder.mutation<unknown, UpdateInvestmentArgs>({
      query: ({ id, payload }) => ({
        url: "/investments/" + id,
        method: "patch",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateInvestment(id),
    }),

    updateInvestmentAccountStatus: builder.mutation<
      void,
      UpdateInvestmentAccountStatusArgs
    >({
      query: ({ id, isActive }) => ({
        url: "/investments/" + id,
        method: "patch",
        data: { isActive },
      }),
      /**
       * Optimistic toggle: patch the *detail* query only, never the list.
       * `getInvestments` takes `{ activeOnly, year }`, so it has many cache
       * entries, and flipping `isActive` changes list membership — on an
       * `activeOnly` list the row should disappear, not sit there flipped.
       * `invalidatesTags` refetches the list with correct membership.
       */
      async onQueryStarted({ id, isActive }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          investmentsApi.util.updateQueryData("getInvestment", id, (draft) => {
            draft.isActive = isActive;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => invalidateInvestment(id),
    }),

    deleteInvestment: builder.mutation<unknown, number>({
      query: (id) => ({
        url: "/investments/" + id,
        method: "delete",
      }),
      invalidatesTags: invalidateDeletedInvestment,
    }),

    createMarketUpdate: builder.mutation<unknown, CreateMarketUpdateArgs>({
      query: ({ id, payload }) => ({
        url: "/investments/" + id + "/market_updates",
        method: "post",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateInvestmentSubResource(id),
    }),

    updateMarketUpdate: builder.mutation<unknown, UpdateMarketUpdateArgs>({
      query: ({ id, marketUpdateId, payload }) => ({
        url: "/investments/" + id + "/market_updates/" + marketUpdateId,
        method: "put",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateInvestmentSubResource(id),
    }),

    deleteMarketUpdate: builder.mutation<unknown, DeleteMarketUpdateArgs>({
      query: ({ id, marketUpdateId }) => ({
        url: "/investments/" + id + "/market_updates/" + marketUpdateId,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateInvestmentSubResource(id),
    }),
  }),
});

export const {
  useGetInvestmentsQuery,
  useGetInvestmentsHistoryQuery,
  useGetInvestmentsYearsQuery,
  useGetInvestmentTypesQuery,
  useGetInvestmentQuery,
  useGetInvestmentHistoryQuery,
  useGetInvestmentTransfersQuery,
  useLazyGetInvestmentTransfersQuery,
  useGetInvestmentMarketUpdatesQuery,
  useLazyGetInvestmentMarketUpdatesQuery,
  useCreateInvestmentMutation,
  useUpdateInvestmentMutation,
  useUpdateInvestmentAccountStatusMutation,
  useDeleteInvestmentMutation,
  useCreateMarketUpdateMutation,
  useUpdateMarketUpdateMutation,
  useDeleteMarketUpdateMutation,
} = investmentsApi;
